import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export type RecurrencePeriod = "MENSUAL" | "BIMESTRAL" | "TRIMESTRAL" | "SEMESTRAL" | "ANUAL" | "PERSONALIZADO";
export type ClientServiceStatus = "ACTIVE" | "PAUSED" | "CANCELLED" | "COMPLETED";

export const RECURRENCE_LABEL: Record<RecurrencePeriod, string> = {
  MENSUAL: "Mensual",
  BIMESTRAL: "Bimestral",
  TRIMESTRAL: "Trimestral",
  SEMESTRAL: "Semestral",
  ANUAL: "Anual",
  PERSONALIZADO: "Personalizado",
};

export const CLIENT_SERVICE_STATUS_LABEL: Record<ClientServiceStatus, string> = {
  ACTIVE: "Activo",
  PAUSED: "Pausado",
  CANCELLED: "Cancelado",
  COMPLETED: "Completado",
};

// --- Catálogo de servicios (organización) ---

export interface ServiceRecord {
  id: string;
  name: string;
  description: string | null;
  recurrence: RecurrencePeriod;
  defaultPrice: string | null;
  isActive: boolean;
  createdAt: string;
}

export function useServices(filters: { search?: string; isActive?: boolean; pageSize?: number } = {}) {
  return useQuery({
    queryKey: ["services", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<ServiceRecord[]>>("/services", { params: filters });
      return data;
    },
  });
}

export interface CreateServicePayload {
  name: string;
  description?: string;
  recurrence?: RecurrencePeriod;
  defaultPrice?: number | string;
  isActive?: boolean;
}

export function useCreateService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateServicePayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<ServiceRecord>>("/services", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["services"] }),
  });
}

export function useUpdateService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: Partial<CreateServicePayload> & { id: string }) => {
      const { data } = await apiClient.patch<ApiSuccessResponse<ServiceRecord>>(`/services/${id}`, payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["services"] }),
  });
}

export function useDeleteService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/services/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["services"] }),
  });
}

// --- Servicios contratados por cliente ---

export interface ClientServiceRecord {
  id: string;
  clientId: string;
  status: ClientServiceStatus;
  price: string | null;
  startDate: string;
  endDate: string | null;
  notes: string | null;
  createdAt: string;
  service: ServiceRecord;
  assignedTo?: { id: string; fullName: string; email: string } | null;
}

export function useClientServices(clientId: string | undefined, filters: { status?: ClientServiceStatus } = {}) {
  return useQuery({
    queryKey: ["clients", clientId, "services", filters],
    enabled: !!clientId,
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<ClientServiceRecord[]>>(`/clients/${clientId}/services`, {
        params: filters,
      });
      return data;
    },
  });
}

export interface ContractServicePayload {
  clientId: string;
  serviceId: string;
  status?: ClientServiceStatus;
  price?: number | string;
  startDate?: string;
  endDate?: string;
  notes?: string;
  assignedToUserId?: string;
}

export function useContractService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ clientId, ...payload }: ContractServicePayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<ClientServiceRecord>>(`/clients/${clientId}/services`, payload);
      return data.data;
    },
    onSuccess: (_data, variables) => queryClient.invalidateQueries({ queryKey: ["clients", variables.clientId, "services"] }),
  });
}

export function useUpdateClientService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      clientId,
      contractId,
      ...payload
    }: Partial<ContractServicePayload> & { clientId: string; contractId: string }) => {
      const { data } = await apiClient.patch<ApiSuccessResponse<ClientServiceRecord>>(
        `/clients/${clientId}/services/${contractId}`,
        payload
      );
      return data.data;
    },
    onSuccess: (_data, variables) => queryClient.invalidateQueries({ queryKey: ["clients", variables.clientId, "services"] }),
  });
}

export function useDeleteClientService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ clientId, contractId }: { clientId: string; contractId: string }) => {
      await apiClient.delete(`/clients/${clientId}/services/${contractId}`);
    },
    onSuccess: (_data, variables) => queryClient.invalidateQueries({ queryKey: ["clients", variables.clientId, "services"] }),
  });
}

export function formatPrice(value: string | null): string {
  if (!value) return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });
}
